import { FlipRoom } from '@/components/pages/kiosk/tablet/v2/ProcedureHandling';
import Avatar from '@material-ui/core/Avatar';
import { makeStyles } from '@material-ui/core/styles';
import { Tooltip, withStyles } from '@material-ui/core';
import React, { memo, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useScope } from '@/hooks/useScope';
import { withTheme } from '@/se/theme';
import responsive from '@/se/utilities/responsive';
import { useQuery } from '@apollo/client';
import { procedureSteps } from '@/graph/procedureSteps';
import isMiddleEast from '../../util/isMiddleEast';
import { STATUSES } from '../entities/patient/enums';
import { ROOM_TYPES, STATUSES as OR_STATUSES } from '../entities/room/enums';
import { OR_TIMES_S } from '../inputs/timeEdit/TimeEditModal';
import Appendix, { FlipRoomTag } from '../pages/kiosk/schedule/overview/Appendix';
import { getOrLateEntryInMinutes } from '../pages/kiosk/schedule/shared/procedureUtils';
import CleaningTime from '../pages/kiosk/tablet/CleaningTime';
import OrRoomReadyTime from '../pages/kiosk/tablet/OrRoomReadyTime';
import { formatDuration as formatDurationTurnover, isSameDate } from '../pages/kiosk/tablet/TurnoverClock';
import { formatEntryTime, formatTimeDuration } from '@/util/duration';
import { isOrTimeActive, optionalFormat, showValueIf } from '../pages/kiosk/tablet/utils';
import { Box, Typography } from '@material-ui/core';
import { useElapsedTime } from '@/util/timerHooks';

const emphasizeHelp = keyframes`
  0% {
    border-color: rgba(255, 0, 0, 0);
    box-shadow: 0 0 4px rgba(255, 0, 0, 0);
  }
  100% {
    border-color: rgba(255, 0, 0, 1);
    box-shadow: 0 0 40px rgba(255, 0, 0, 1);
  }
`;

const emphasizeOrRoomReady = keyframes`
  0% {
    border-color: rgba(255, 0, 0, 0);
    box-shadow: 0 0 4px rgba(255, 0, 0, 0);
  }
  100% {
    border-color: rgba(0, 255, 0, 1);
    box-shadow: 0 0 40px rgba(0, 255, 0, 1);
  }
`;

export const Container = styled.div`
  background-color: ${withTheme(theme => theme.list.backgroundColor.string())};
  border-radius: 0.125rem;
  display: flex;
  flex-flow: column;
  flex: 1 0 0;
  padding: ${props => (props.compact ? '1.25rem 1.5rem' : '1.5vh')};
  overflow-x: hidden;
  position: relative;
  border: ${props => (props.procedureColor ? `5px solid ${props.procedureColor}` : 'transparent')};

  ${props =>
    props.isHelp
      ? css`
          border: 4px solid transparent;
          animation: 1.2s ${emphasizeHelp} ease-in alternate infinite;
        `
      : props?.isOrRoomReady
        ? css`
            border: 4px solid transparent;
            animation: 1.2s ${emphasizeOrRoomReady} ease-in alternate infinite;
          `
        : null};

  @media (max-height: 900px) {
    padding-top: 1.5vh;
    padding-bottom: 1.5vh;
  }

  ${props => props.fusePanels && 'margin-right: -0.25rem !important;'}

  ${responsive.lg.andSmaller`
    padding-left: 2vw;
    padding-right: 2vw;
  `};

  ${responsive.sm.andSmaller`
    grid-template-columns: auto 100% auto;
    padding-left: 1rem;
    padding-right: 1rem;
  `};

  ${props =>
    props.noPadding &&
    css`
      padding-left: 0;
      padding-right: 0;
    `}

  h3 {
    opacity: 1;
  }

  ${props =>
    props.miniView &&
    css`
      padding: 0.5rem 1rem;
      flex: 1 1 calc(100% / ${props => props.numberOfPanels} - 0.5rem);
    `}
`;

const Header = styled.div`
  min-height: 3.65ch;
  ${props =>
    props.noPadding &&
    css`
      padding-left: ${props => (props.compact ? '1.5rem' : '2rem')};
      padding-right: ${props => (props.compact ? '1.5rem' : '2rem')};
    `}
  margin-bottom: ${props => (props.compactView ? '0' : '1.5rem')};

  @media (max-height: 900px) {
    margin-bottom: 1.5vh;
  }
`;

const Main = styled.div`
  flex: 1;
`;

const Info = styled.div`
  display: flex;
  align-items: ${props => (!props.timeEntered ? 'center' : 'end')};
  justify-content: space-between;
  flex: 1 0 auto;
`;

// Simple CSS animation to replace react-pose animations
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Status = styled.h2`
  font-size: 2em;
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin: 0;
  // margin-top: -1.5rem;
  // color: white;

  color: ${withTheme(theme => theme.textColor.string())};

  @media (max-height: 900px) {
    font-size: 1.125rem;
    // margin-top: -1.5vh;
  }

  ${responsive.lg.andSmaller`
    font-size: 2.5vw;
  `};

  ${responsive.sm.andSmaller`
    font-size: 1rem;
  `};

  @media (max-height: 600px) {
    font-size: 3vh;
  }

  ${props =>
    props.miniView &&
    css`
      font-size: 1rem;
    `}
  animation: ${fadeInUp} 500ms ease forwards;
`;

export const Title = styled.h3`
  font-size: ${props => (props.compact ? '.875em' : '1.5em')};
  line-height: ${props => (props.compact ? '1rem' : '1.55')};
  text-transform: ${props => (props.compact ? 'uppercase' : 'none')};
  letter-spacing: ${props => (props.compact ? '.44px' : '0')};
  font-weight: ${props => (props.compact ? '500' : 'initial')};
  margin-bottom: ${props => (props.compact ? '.875rem' : '0')};
  color: ${props => (props.color ? props.color : withTheme(theme => theme.textColor.string()))};
  opacity: 0.5;
  margin-top: 0;
  margin-right: 0.5em;

  @media (max-height: 900px) {
    font-size: 1rem;
  }

  ${responsive.lg.andSmaller`
    font-size: 2vw;
  `};

  ${responsive.sm.andSmaller`
    font-size: .875rem;
  `};

  @media (max-height: 600px) {
    font-size: 3vh;
  }
`;

const Meta = styled.span`
  font-size: 1.125em;
  color: ${withTheme(theme => theme.textColor.string())};
  opacity: 0.5;

  @media (max-height: 600px) {
    font-size: 3vh;
  }
`;

const Times = styled.div`
  flex-flow: column;
  color: white;
  white-space: nowrap;
  display: ${props => (props.compactView ? 'none' : 'initial')};

  > * + * {
    margin-top: 0.75rem;
  }
`;

const Entry = styled.div`
  display: flex;
  color: #abe3ff;
  justify-content: flex-end;

  > * {
    flex: 1 0 65%;
  }
`;

const Label = styled.div`
  margin-right: 0.5rem;
  text-align: right;
  opacity: 0.5;
`;

const Time = styled.div`
  color: white;
  text-align: right;
  flex: 1 0 35%;
`;

const HeaderTitle = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  overflow: hidden;
  text-overflow: ellipsis;

  ${props =>
    props.miniView &&
    css`
      font-size: 0.75rem;
    `}
`;

const BiggerLabel = styled.div`
  opacity: 0.75;
  font-size: 1.5em;
`;

export const getColor = (status, hospitalId) => {
  const applyColor =
    window.location.hostname === 'localhost' || // show colors and borders on localhost for all the test hospitals
    (window.location.hostname === 'testing.ospitek.com' && hospitalId === 10) ||
    (window.location.hostname === 'testing.ospitek.com' && hospitalId === 135) ||
    (window.location.hostname === 'testing.ospitek.com' && hospitalId === 138) ||
    (window.location.hostname === 'view2.ospitek.com' && hospitalId === 7) ||
    (window.location.hostname === 'view3.ospitek.com' && hospitalId === 3) ||
    (window.location.hostname === 'view3.ospitek.com' && hospitalId === 10) ||
    (window.location.hostname === 'view4.ospitek.com' && hospitalId === 2) ||
    (window.location.hostname === 'view4.ospitek.com' && hospitalId === 3) ||
    (window.location.hostname === 'view4.ospitek.com' && hospitalId === 10) ||
    (window.location.hostname === 'view4.ospitek.com' && hospitalId === 16) ||
    (window.location.hostname === 'view4.ospitek.com' && hospitalId === 22) ||
    (window.location.hostname === 'view5.ospitek.com' && hospitalId === 1) ||
    (window.location.hostname === 'view5.ospitek.com' && hospitalId === 2) ||
    (window.location.hostname === 'view5.ospitek.com' && hospitalId === 4) ||
    (window.location.hostname === 'view5.ospitek.com' && hospitalId === 13) ||
    (window.location.hostname === 'view5.ospitek.com' && hospitalId === 14);

  const applyCustomColors =
    (window.location.hostname === 'view5.ospitek.com' && hospitalId === 6) ||
    (window.location.hostname === 'view5.ospitek.com' && hospitalId === 7) ||
    (window.location.hostname === 'view5.ospitek.com' && hospitalId === 8) ||
    (window.location.hostname === 'testing.ospitek.com' && hospitalId === 158);

  const applyView4Hospital15Colors =
    (window.location.hostname === 'view4.ospitek.com' && hospitalId === 15) ||
    (window.location.hostname === 'testing.ospitek.com' && hospitalId === 171);

  if (applyView4Hospital15Colors) {
    switch (status) {
      case 'WR':
      case 'Admitted':
        return '#A020F0';
      case 'PRE-OP':
        return '#0047AB';
      case 'OR':
      case 'InOr':
      case 'AnestStart':
      case 'PhysIn':
      case 'TimeOut':
      case 'AntibioticTime':
      case 'Ready':
      case 'Ongoing':
        return '#00FF00';
      case 'PACU':
      case 'Closing':
      case 'SurgeonLeft':
      case 'SurgeonLeftOR':
      case 'End':
      case 'ProcedureEnd':
      case 'AnestEnd':
        return '#ffbf00';
      default:
        return null;
    }
  }

  if (applyCustomColors) {
    switch (status) {
      case 'InOr':
      case 'AnestStart':
      case 'PhysIn':
      case 'TimeOut':
      case 'AntibioticTime':
      case 'Ongoing':
        return '#F55323';
      case 'Closing':
      case 'End':
      case 'AnestEnd':
        return '#FF2C2C';
      default:
        return null;
    }
  }

  if (applyColor) {
    const view3Org3 =
      (window.location.hostname === 'view3.ospitek.com' && hospitalId === 3) ||
      (window.location.hostname === 'testing.ospitek.com' && hospitalId === 138);

    switch (status) {
      case 'InOr':
        return '#0047AB';
      case 'AnestStart':
      case 'PhysIn':
      case 'TimeOut':
      case 'AntibioticTime':
      case 'Ongoing':
        return '#00FF00';
      case 'Closing':
        return '#ffbf00';
      case 'Dressing':
      case 'DressingOn':
        return '#A020F0';
      case 'End':
      case 'ProcedureEnd':
      case 'AnestEnd':
        return view3Org3 ? '#A020F0' : '#F55323';
      default:
        return null;
    }
  }

  return null;
};

const formatTimeRange = (startTime, durationMinutes) => {
  if (!startTime || !durationMinutes) return '';

  const start = new Date(startTime);
  const end = new Date(start.getTime() + durationMinutes * 60000);

  return `${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} -
          ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}`;
};

const StyledTooltip = withStyles(theme => ({
  tooltip: {
    fontSize: '14px',
    fontWeight: 400,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.9)',
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 2),
    borderRadius: 8,
    border: theme.palette.type === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(10px)',
    maxWidth: '200px',
    textAlign: 'center',
    boxShadow: theme.palette.type === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.4)',
  },
  arrow: {
    color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.9)',
  },
}))(Tooltip);

export function Timer({ startTime }) {
  // Use the proper useElapsedTime hook for elapsed time calculations
  const { elapsedSeconds } = useElapsedTime({
    startTime: startTime || new Date(),
    interval: 1000,
    precision: 'second',
  });

  return <span style={{ marginLeft: 4 }}>{formatDurationTurnover(elapsedSeconds, { dropZeroes: true })}</span>;
}

const StaffIndicatorTooltip = memo(function StaffIndicatorTooltip({ name, title, fullTitle, entryTime, timeOfDay }) {
  const formattedEntryTime = useMemo(() => formatEntryTime(entryTime), [entryTime]);
  const formattedTimeAtLocation = useMemo(() => formatTimeDuration(entryTime, timeOfDay), [entryTime, timeOfDay]);
  return (
    <div>
      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{name || 'Unknown'}</div>
      <div style={{ marginBottom: '2px' }}>Title: {fullTitle || title}</div>
      <div style={{ marginBottom: '2px' }}>Entry Time: {formattedEntryTime}</div>
      <div>Time at Location: {formattedTimeAtLocation}</div>
    </div>
  );
});

const StaffIndicators = memo(function StaffIndicators({ roomStaff }) {
  const classes = useStyles();

  if (!roomStaff || roomStaff.length === 0) {
    return null;
  }

  return (
    <Box className={classes.staffGroup} display="flex" alignItems="center">
      {roomStaff.map(staff => (
        <StyledTooltip
          disablePortal
          key={staff.id || `${staff.type}-${staff.entityId}`}
          title={
            <StaffIndicatorTooltip
              name={staff.name}
              title={staff.name}
              fullTitle={staff.fullTitle}
              entryTime={staff.entryTime}
              timeOfDay={staff.timeOfDay}
            />
          }
          placement="top"
          arrow
        >
          <Avatar className={classes.staffIndicator}>{staff.text}</Avatar>
        </StyledTooltip>
      ))}
    </Box>
  );
});

const PanelContainer = ({
  title,
  capacity,
  timeEntered,
  patients,
  children,
  status,
  compact,
  timeoutTime,
  enteredTime,
  procedureStartTime,
  closingTime,
  statusLabel,
  compactView,
  miniView,
  scheduleProcedure,
  numberOfPanels,
  Stats,
  style,
  noPadding,
  titleColor = undefined,
  fusePanels = false,
  preOpFocused,
  pacuFocused,
  isHelp,
  isFlipRoom,
  flipRoomTimestamp,
  room,
  messagingView,
  hideTurnoverClock,
  patientId,
  patientData,
  showScheduledProcedureDuration,
  roomStaff = [],
}) => {
  const scope = useScope();
  const hospitalId = scope?.hospital?.id;
  const isVacant = status === OR_STATUSES.VACANT;
  const isCleaning = status === OR_STATUSES.CLEANING;
  const procedureColor = scheduleProcedure?.color || getColor(status, hospitalId);

  const { data: procedureStepsData } = useQuery(procedureSteps);

  const isOrRoomReadyEnabled = procedureStepsData?.procedureSteps?.some(step => step.type === 'OR_ROOM_READY');

  return (
    <Container
      fusePanels={fusePanels}
      compact={compact}
      miniView={miniView}
      numberOfPanels={numberOfPanels}
      style={style}
      noPadding={noPadding}
      isHelp={isHelp}
      isOrRoomReady={
        room?.orRoomReady &&
        isVacant &&
        isSameDate(new Date(room?.cleaningStartedAt || room?.turnoverStartedAt), new Date())
      }
      procedureColor={isVacant || isCleaning ? null : procedureColor}
    >
      <Header compactView={compactView} noPadding={noPadding}>
        <Info capacity={capacity}>
          <Main miniView={miniView}>
            <Box display="flex" alignItems="start">
              <Box flex={1}>
                <HeaderTitle miniView={miniView}>
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    {title ? ( // It’s hackish
                      <Title color={titleColor} compact={compact}>
                        {title}
                      </Title>
                    ) : (
                      <Title hidden color={'transparent'} compact={compact}>
                        Stub title
                      </Title>
                    )}
                    <StaffIndicators roomStaff={roomStaff} />
                  </Box>
                  {Stats && Stats}
                </HeaderTitle>
                <Status miniView={miniView} isReady={status === STATUSES.READY}>
                  {room?.orRoomReady &&
                  isVacant &&
                  isSameDate(new Date(room?.cleaningStartedAt || room?.turnoverStartedAt), new Date())
                    ? 'OR Ready'
                    : statusLabel}
                </Status>
              </Box>
              {isFlipRoom && (
                <FlipRoomTag style={{ marginRight: '0' }}>
                  <Box display="flex" alignItems="center" style={{ gap: 2 }}>
                    <FlipRoom style={{ width: '1.5rem', height: '1.5rem' }} />
                    FR
                    <Box style={{ minWidth: '5ch' }}>
                      <Timer startTime={flipRoomTimestamp} />
                    </Box>
                  </Box>
                </FlipRoomTag>
              )}
            </Box>
            {showScheduledProcedureDuration && patientData?.procedure && patientId && (
              <Typography variant="body1">
                <BiggerLabel>
                  Scheduled: {formatTimeRange(patientData?.procedure?.startTime, patientData?.procedure?.duration)}
                </BiggerLabel>
              </Typography>
            )}
            {(status === 'Cleaning' || (status === 'Vacant' && room?.turnoverStartedAt)) &&
              !hideTurnoverClock &&
              isSameDate(new Date(room?.cleaningStartedAt || room?.turnoverStartedAt), new Date()) && (
                <CleaningTime room={room} />
              )}
            {status === 'Vacant' &&
              isOrRoomReadyEnabled &&
              room?.turnoverStartedAt &&
              !hideTurnoverClock &&
              isSameDate(new Date(room?.cleaningStartedAt || room?.turnoverStartedAt), new Date()) && (
                <OrRoomReadyTime room={room} />
              )}
            <Appendix
              isHelp={scheduleProcedure?.patient?.room?.type === ROOM_TYPES.OR && isHelp}
              late={getOrLateEntryInMinutes(scheduleProcedure, isMiddleEast(hospitalId))}
              style={{ backgroundColor: 'transparent' }}
              messagingView={messagingView}
              scheduleProcedure={scheduleProcedure}
              isMiddleEast={isMiddleEast(hospitalId)}
            />
          </Main>
          {capacity || !timeEntered ? (
            !fusePanels && (
              <Meta>
                {patients}
                {capacity ? '/' : ''}
                {capacity}
              </Meta>
            )
          ) : (
            <Times compactView={compactView}>
              <Entry>
                <Label>Entered</Label>
                <Time>
                  {showValueIf(optionalFormat(enteredTime, 'HH:mm'), isOrTimeActive(status, OR_TIMES_S.ENTERED_AT))}
                </Time>
              </Entry>
              <Entry>
                <Label>Time Out</Label>
                <Time>
                  {showValueIf(optionalFormat(timeoutTime, 'HH:mm'), isOrTimeActive(status, OR_TIMES_S.TIMEOUT_START))}
                </Time>
              </Entry>
              <Entry>
                <Label>Procedure</Label>
                <Time>
                  {showValueIf(
                    optionalFormat(procedureStartTime, 'HH:mm'),
                    isOrTimeActive(status, OR_TIMES_S.PROCEDURE_START)
                  )}
                </Time>
              </Entry>
              <Entry>
                <Label>Closing</Label>
                <Time>
                  {showValueIf(optionalFormat(closingTime, 'HH:mm'), isOrTimeActive(status, OR_TIMES_S.CLOSING_START))}
                </Time>
              </Entry>
            </Times>
          )}
        </Info>
      </Header>
      {children}
    </Container>
  );
};

const useStyles = makeStyles(theme => ({
  staffGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.35),
    marginLeft: '-0.5rem',
  },
  staffIndicator: {
    minWidth: theme.spacing(4.8),
    width: 'auto',
    height: theme.spacing(3.6),
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium,
    backgroundColor: theme.palette.type === 'dark' ? '#495173' : '#3f51b5',
    color: theme.palette.common.white,
    border: `2px solid ${theme.palette.type === 'dark' ? '#00A7F7' : '#1976d2'}`,
    borderRadius: '4px',
    padding: '0 4.8px',
    boxShadow: theme.palette.type === 'dark' ? '0 2px 4px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
}));

export default PanelContainer;
